import React, { useState, useRef, useCallback, useEffect } from 'react';
import './App.css';
import PlayerHand from './components/PlayerHand.jsx';
import AuthButton from './components/AuthButton.jsx';
import ThemePicker from './components/ThemePicker.jsx';
import LanguageToggle from './components/LanguageToggle.jsx';
import { useTheme } from './contexts/ThemeContext.jsx';
import { useTranslation } from './i18n/index.jsx';
import { createDeck, shuffleDeck, dealCards } from './utils/cardUtils';
import { selectCard } from './ai/strategies';
import { getTodayDate, createDailyRng, getDailyChallengeConfig, seededShuffle } from './utils/seededRandom';
import {
  calculateCardValue,
  getSpecialEffect,
  getNextPlayerIndex,
  handlePlayerLoss
} from './utils/gameLogic';
import {
  trackGameStarted,
  trackRoundBusted,
  trackGameOver,
  trackHandPlayed,
  trackProUpsellShown,
  trackPlayerCountChanged,
  trackDailyChallengeStarted,
} from './utils/analytics';

const FLY_DURATION = 450;
const DRAW_DURATION = 400;
function getSeatDir(playerIndex, totalPlayers) {
  if (playerIndex === 0) return 'bottom';
  const aiIndex = playerIndex - 1;
  const aiCount = totalPlayers - 1;
  const seatClass = getAISeatClass(aiIndex, aiCount);
  if (seatClass.includes('left')) return 'left';
  if (seatClass.includes('right')) return 'right';
  return 'top';
}

// Position AI players around the table like a real card game
// Seats go clockwise from player's left: left, top-left, top, top-right, right, etc.
const SEAT_POSITIONS = [
  ['seat-top'],                                                          // 1 AI
  ['seat-left', 'seat-right'],                                           // 2 AI
  ['seat-left', 'seat-top', 'seat-right'],                               // 3 AI
  ['seat-left', 'seat-top-left', 'seat-top-right', 'seat-right'],        // 4 AI
  ['seat-left', 'seat-top-left', 'seat-top', 'seat-top-right', 'seat-right'], // 5 AI
  ['seat-left', 'seat-top-left', 'seat-top', 'seat-top-right', 'seat-right', 'seat-far-left'],  // 6 AI
  ['seat-far-left', 'seat-left', 'seat-top-left', 'seat-top', 'seat-top-right', 'seat-right', 'seat-far-right'], // 7 AI
  ['seat-far-left', 'seat-left', 'seat-top-left', 'seat-top', 'seat-top-right', 'seat-right', 'seat-far-right', 'seat-bottom-left'], // 8 AI
];

function getAISeatClass(aiIndex, aiCount) {
  const positions = SEAT_POSITIONS[aiCount - 1] || SEAT_POSITIONS[SEAT_POSITIONS.length - 1];
  return positions[aiIndex] || 'seat-top';
}

function App({ hasAuth = false }) {
  const { themeId, isPro } = useTheme();
  const { t, lang } = useTranslation();
  const [difficulty, setDifficulty] = useState(() => localStorage.getItem('game99_difficulty') || 'easy');
  const [playerCount, setPlayerCount] = useState(() => parseInt(localStorage.getItem('game99_players')) || 4);
  const [showProUpsell, setShowProUpsell] = useState(false);

  const [gameState, setGameState] = useState({
    currentSum: 0,
    players: [],
    currentPlayerIndex: 0,
    deck: [],
    gameDirection: 1,
    gameOver: false,
    winner: null,
    gameStarted: false
  });

  const [playerHand, setPlayerHand] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [gameMessage, setGameMessage] = useState('');
  const [playedCards, setPlayedCards] = useState([]);
  const [flyingCard, setFlyingCard] = useState(null);
  const [drawingCard, setDrawingCard] = useState(null); // { direction }
  const [aceChoice, setAceChoice] = useState(null); // { card, resolve }
  const [openDropdown, setOpenDropdown] = useState(null); // 'lang' | 'theme' | 'profile' | null
  const [dailyMode, setDailyMode] = useState(null); // { date, config } or null
  const isAnimating = useRef(false);
  const gameConfig = useRef({ cardsPerHand: 3 });

  const launchFlyingCard = useCallback((card, playerIndex, totalPlayers) => {
    return new Promise(resolve => {
      setFlyingCard({ card, direction: getSeatDir(playerIndex, totalPlayers) });
      setTimeout(() => {
        setFlyingCard(null);
        setPlayedCards(prev => [...prev, card]);
        resolve();
      }, FLY_DURATION);
    });
  }, []);

  const launchDrawCard = useCallback((playerIndex, totalPlayers) => {
    return new Promise(resolve => {
      setDrawingCard({ direction: getSeatDir(playerIndex, totalPlayers) });
      setTimeout(() => {
        setDrawingCard(null);
        resolve();
      }, DRAW_DURATION);
    });
  }, []);

  const initializeGame = (opts = {}) => {
    const diff = opts.difficulty || difficulty;
    const pc = opts.playerCount || playerCount;
    const cardsPerHand = opts.cardsPerHand || 3;
    const useRng = opts.rng || null;

    const rawDeck = createDeck(diff);
    const deck = useRng ? seededShuffle(rawDeck, useRng) : shuffleDeck(rawDeck);

    const lives = opts.daily ? 1 : 3;
    const players = [
      { id: 0, name: t('you'), isHuman: true, hand: [], isAlive: true, score: lives },
    ];
    for (let i = 1; i < pc; i++) {
      players.push({ id: i, name: `P${i}`, isHuman: false, hand: [], isAlive: true, score: lives });
    }

    gameConfig.current = { cardsPerHand };
    const { updatedPlayers, remainingDeck } = dealCards(players, deck, cardsPerHand);

    setGameState({
      currentSum: 0,
      players: updatedPlayers,
      currentPlayerIndex: 0,
      deck: remainingDeck,
      gameDirection: 1,
      gameOver: false,
      winner: null,
      gameStarted: true
    });

    setPlayerHand(updatedPlayers[0].hand);
    setSelectedCard(null);
    setPlayedCards([]);
    setFlyingCard(null);
    setDrawingCard(null);
    isAnimating.current = false;
    trackGameStarted({ difficulty: diff, playerCount: pc, isDaily: !!opts.daily });
    setGameMessage({ key: 'yourTurn' });
  };

  const startNewRound = (players) => {
    const alivePlayers = players.filter(player => player.score > 0);

    if (alivePlayers.length <= 1) {
      setGameState(prev => ({
        ...prev,
        gameOver: true,
        winner: alivePlayers[0] || null,
        players: players
      }));
      setGameMessage(alivePlayers.length === 1 ? { key: 'wins', name: alivePlayers[0].name } : { key: 'gameOver' });
      if (alivePlayers[0]) {
        trackGameOver({ winnerName: alivePlayers[0].name, isHumanWinner: alivePlayers[0].isHuman });
      }
      isAnimating.current = false;
      return;
    }

    const deck = shuffleDeck(createDeck(difficulty));
    const { updatedPlayers, remainingDeck } = dealCards(players, deck, gameConfig.current.cardsPerHand);

    setGameState(prev => ({
      ...prev,
      currentSum: 0,
      players: updatedPlayers,
      currentPlayerIndex: 0,
      deck: remainingDeck,
      gameDirection: 1
    }));

    setPlayerHand(updatedPlayers[0].hand);
    setSelectedCard(null);
    setPlayedCards([]);
    setFlyingCard(null);
    setDrawingCard(null);
    isAnimating.current = false;
    setGameMessage({ key: 'newRound' });
  };

  const restartGame = () => {
    setDailyMode(null);
    setGameState({
      currentSum: 0,
      players: [],
      currentPlayerIndex: 0,
      deck: [],
      gameDirection: 1,
      gameOver: false,
      winner: null,
      gameStarted: false
    });
    setPlayerHand([]);
    setSelectedCard(null);
    setPlayedCards([]);
    setFlyingCard(null);
    setDrawingCard(null);
    isAnimating.current = false;
    setGameMessage(null);
  };

  const drawCard = (playerIndex, players, deck) => {
    if (deck.length === 0) return { players, deck };

    const newCard = deck[deck.length - 1];
    const newDeck = deck.slice(0, -1);
    const newPlayers = [...players];
    newPlayers[playerIndex].hand.push(newCard);

    return { players: newPlayers, deck: newDeck };
  };

  const playCard = async (card) => {
    if (!gameState.gameStarted || gameState.gameOver || gameState.currentPlayerIndex !== 0) {
      return;
    }
    if (isAnimating.current) return;
    isAnimating.current = true;

    // If Ace, let user choose 1 or 11
    let chosenAceValue = null;
    if (card.value === 'A') {
      const canPlay11 = gameState.currentSum + 11 <= 99;
      const canPlay1 = gameState.currentSum + 1 <= 99;
      // If both options are valid, ask the user
      if (canPlay11 && canPlay1) {
        chosenAceValue = await new Promise(resolve => {
          setAceChoice({ card, resolve });
        });
        setAceChoice(null);
      } else {
        chosenAceValue = canPlay11 ? 11 : 1;
      }
    }

    const cardValue = chosenAceValue || calculateCardValue(card, gameState.currentSum);
    const newSum = Math.max(0, gameState.currentSum + cardValue);
    const specialEffect = getSpecialEffect(card);

    // Remove card from hand immediately (now 2 cards)
    const newPlayerHand = playerHand.filter(c => c.id !== card.id);
    setPlayerHand(newPlayerHand);

    // Fly the played card to center
    await launchFlyingCard(card, 0, gameState.players.length);

    trackHandPlayed({ cardValue: card.value, cardEffect: specialEffect || 'none', sum: newSum, isHuman: true });

    let updatedPlayers = [...gameState.players];
    updatedPlayers[0].hand = newPlayerHand;

    // Update score immediately after card lands
    let newDirection = gameState.gameDirection;
    let skipNext = false;

    if (newSum > 99) {
      updatedPlayers[0] = handlePlayerLoss(updatedPlayers[0]);
      trackRoundBusted({ playerName: t('you'), sum: newSum });
      setGameMessage({ key: 'bust', text: `${newSum} > 99` });
      setGameState(prev => ({ ...prev, currentSum: newSum, players: updatedPlayers }));

      setTimeout(() => {
        startNewRound(updatedPlayers);
      }, 2000);
      return;
    }

    if (specialEffect === 'reverse') {
      newDirection = -newDirection;
      setGameMessage({ key: 'reverse' });
    } else if (specialEffect === 'skip') {
      skipNext = true;
      setGameMessage({ key: 'skip' });
    } else if (card.value === 'K') {
      setGameMessage({ raw: '= 99!' });
    } else {
      setGameMessage({ raw: `${cardValue >= 0 ? '+' : ''}${cardValue}` });
    }

    // Update sum on screen before drawing
    setGameState(prev => ({ ...prev, currentSum: newSum, gameDirection: newDirection, players: updatedPlayers }));

    // Then draw a replacement card with animation
    let finalPlayers = updatedPlayers;
    let finalDeck = gameState.deck;

    if (finalDeck.length > 0) {
      await launchDrawCard(0, gameState.players.length);
      const drawResult = drawCard(0, finalPlayers, finalDeck);
      finalPlayers = drawResult.players;
      finalDeck = drawResult.deck;
      setPlayerHand(finalPlayers[0].hand);
    }

    const nextPlayerIndex = getNextPlayerIndex(
      gameState.currentPlayerIndex,
      finalPlayers,
      newDirection,
      skipNext
    );

    setGameState(prev => ({
      ...prev,
      currentSum: newSum,
      players: finalPlayers,
      currentPlayerIndex: nextPlayerIndex,
      deck: finalDeck,
      gameDirection: newDirection
    }));

    setSelectedCard(null);

    setTimeout(() => {
      playAITurn(nextPlayerIndex, finalPlayers, finalDeck, newSum, newDirection);
    }, 400);
  };

  const playAITurn = async (playerIndex, players, deck, currentSum, direction) => {
    if (playerIndex === 0 || players[playerIndex].score <= 0) {
      isAnimating.current = false;
      return;
    }

    const aiPlayer = players[playerIndex];
    const bestCard = selectCard(difficulty, aiPlayer.hand, currentSum, players, playerIndex, direction);

    if (!bestCard) { isAnimating.current = false; return; }

    const cardValue = calculateCardValue(bestCard, currentSum);
    const newSum = Math.max(0, currentSum + cardValue);
    const specialEffect = getSpecialEffect(bestCard);

    // Remove from AI hand (now 2 cards)
    const newPlayers = [...players];
    newPlayers[playerIndex].hand = newPlayers[playerIndex].hand.filter(c => c.id !== bestCard.id);
    setGameState(prev => ({ ...prev, players: newPlayers }));

    // Fly the played card to center
    await launchFlyingCard(bestCard, playerIndex, players.length);

    if (newSum > 99) {
      newPlayers[playerIndex] = handlePlayerLoss(newPlayers[playerIndex]);
      trackRoundBusted({ playerName: aiPlayer.name, sum: newSum });
      setGameMessage({ key: 'bust', name: aiPlayer.name, text: `${newSum}` });
      setGameState(prev => ({ ...prev, currentSum: newSum, players: newPlayers }));

      setTimeout(() => {
        startNewRound(newPlayers);
      }, 2000);
      return;
    }

    let newDirection = direction;
    let skipNext = false;

    if (specialEffect === 'reverse') {
      newDirection = -newDirection;
      setGameMessage({ key: 'reverse' });
    } else if (specialEffect === 'skip') {
      skipNext = true;
      setGameMessage({ key: 'skip' });
    } else if (bestCard.value === 'K') {
      setGameMessage({ raw: '= 99!' });
    } else {
      setGameMessage({ raw: `${cardValue >= 0 ? '+' : ''}${cardValue}` });
    }

    // Update score immediately (before drawing)
    setGameState(prev => ({ ...prev, currentSum: newSum, players: newPlayers, gameDirection: newDirection }));

    // Draw replacement with animation
    let finalPlayers = newPlayers;
    let finalDeck = deck;

    if (finalDeck.length > 0) {
      await launchDrawCard(playerIndex, players.length);
      const drawResult = drawCard(playerIndex, finalPlayers, finalDeck);
      finalPlayers = drawResult.players;
      finalDeck = drawResult.deck;
    }

    const finalNextPlayerIndex = getNextPlayerIndex(playerIndex, finalPlayers, newDirection, skipNext);

    setGameState(prev => ({
      ...prev,
      players: finalPlayers,
      currentPlayerIndex: finalNextPlayerIndex,
      deck: finalDeck,
    }));

    if (finalNextPlayerIndex === 0) {
      let humanPlayers = finalPlayers;
      let humanDeck = finalDeck;

      if (humanPlayers[0].hand.length < 3 && humanDeck.length > 0) {
        await launchDrawCard(0, players.length);
        const drawResult = drawCard(0, humanPlayers, humanDeck);
        humanPlayers = drawResult.players;
        humanDeck = drawResult.deck;
        setGameState(prev => ({ ...prev, players: humanPlayers, deck: humanDeck }));
      }

      setPlayerHand(humanPlayers[0].hand);
      isAnimating.current = false;
      setTimeout(() => { setGameMessage({ key: 'yourTurn' }); }, 300);
    } else {
      setTimeout(() => {
        playAITurn(finalNextPlayerIndex, finalPlayers, finalDeck, newSum, newDirection);
      }, 200);
    }
  };

  const getSumColor = () => {
    if (gameState.currentSum >= 90) return '#ff4444';
    if (gameState.currentSum >= 75) return '#ff8800';
    if (gameState.currentSum >= 50) return '#ffaa00';
    return '#44ff44';
  };

  return (
    <div className={`App ${themeId}`} dir={lang === 'he' ? 'rtl' : 'ltr'}>
      <div className="top-bar">
        <button className="top-bar-home" onClick={restartGame}>
          <span className="top-bar-logo">#99</span>
        </button>
        <div className="top-bar-right">
          <LanguageToggle open={openDropdown === 'lang'} onToggle={() => setOpenDropdown(d => d === 'lang' ? null : 'lang')} onClose={() => setOpenDropdown(null)} />
          <ThemePicker open={openDropdown === 'theme'} onToggle={() => setOpenDropdown(d => d === 'theme' ? null : 'theme')} onClose={() => setOpenDropdown(null)} />
          {hasAuth && <AuthButton open={openDropdown === 'profile'} onToggle={() => setOpenDropdown(d => d === 'profile' ? null : 'profile')} onClose={() => setOpenDropdown(null)} />}
        </div>
      </div>
      {!gameState.gameStarted ? (
        <div className="start-screen">
          <div className="start-content">
            <h1 className="game-title">{t('title')}</h1>
            <p className="game-subtitle">{t('subtitle')}</p>

            <div className="rules-compact">
              <div className="rule-row">
                <span className="rule-card-mini">2-8</span>
                <span>{t('faceValue')}</span>
              </div>
              <div className="rule-row">
                <span className="rule-card-mini special">4</span>
                <span>{t('ruleSkip')}</span>
              </div>
              <div className="rule-row">
                <span className="rule-card-mini special">9</span>
                <span>{t('ruleReverse')}</span>
              </div>
              <div className="rule-row">
                <span className="rule-card-mini special">10</span>
                <span>-10</span>
              </div>
              <div className="rule-row">
                <span className="rule-card-mini">J/Q</span>
                <span>+10</span>
              </div>
              <div className="rule-row">
                <span className="rule-card-mini special">K</span>
                <span>= 99</span>
              </div>
              <div className="rule-row">
                <span className="rule-card-mini special">A</span>
                <span>+1 or +11</span>
              </div>
            </div>

            <div className="game-options">
              <div className="difficulty-selector">
                {['easy', 'normal', 'hard'].map(d => (
                  <button
                    key={d}
                    className={`diff-btn ${difficulty === d ? 'active' : ''}`}
                    onClick={() => {
                      if (!isPro && d !== 'easy') { setShowProUpsell(true); trackProUpsellShown('difficulty'); return; }
                      setDifficulty(d); localStorage.setItem('game99_difficulty', d);
                    }}
                  >
                    {t(d)}
                  </button>
                ))}
              </div>

              <div className="options-divider" />

              <div className="player-count-stepper">
              <span className="stepper-icon">👤</span>
              <button className="stepper-btn" onClick={() => {
                const n = Math.max(2, playerCount - 1);
                if (!isPro && n !== 4) { setShowProUpsell(true); trackProUpsellShown('player_count'); return; }
                setPlayerCount(n); localStorage.setItem('game99_players', n); trackPlayerCountChanged(n);
              }}>−</button>
              <span className="stepper-value">{playerCount}</span>
              <button className="stepper-btn" onClick={() => {
                const n = Math.min(9, playerCount + 1);
                if (!isPro && n !== 4) { setShowProUpsell(true); trackProUpsellShown('player_count'); return; }
                setPlayerCount(n); localStorage.setItem('game99_players', n); trackPlayerCountChanged(n);
              }}>+</button>
              </div>
            </div>

            <button className="start-button" onClick={() => initializeGame()}>
              {t('play')}
            </button>

            {showProUpsell && (
              <div className="pro-upsell-overlay" onClick={() => setShowProUpsell(false)}>
                <div className="pro-upsell" onClick={e => e.stopPropagation()}>
                  <div className="pro-upsell-badge">PRO</div>
                  <h3 className="pro-upsell-title">Unlock Everything</h3>
                  <p className="pro-upsell-sub">Take your game to the next level</p>
                  <div className="pro-upsell-features">
                    <div className="pro-feature-item">
                      <span className="pro-feature-icon">🎯</span>
                      <div className="pro-feature-text">
                        <strong>{t('proFeatureDifficulty')}</strong>
                        <span>Test your skills against smarter AI</span>
                      </div>
                    </div>
                    <div className="pro-feature-item">
                      <span className="pro-feature-icon">👥</span>
                      <div className="pro-feature-text">
                        <strong>{t('proFeaturePlayers')}</strong>
                        <span>Play with 2–9 players</span>
                      </div>
                    </div>
                    <div className="pro-feature-item">
                      <span className="pro-feature-icon">🎨</span>
                      <div className="pro-feature-text">
                        <strong>{t('proFeatureThemes')}</strong>
                        <span>Neon, Sakura, Dark & more</span>
                      </div>
                    </div>
                    <div className="pro-feature-item">
                      <span className="pro-feature-icon">📅</span>
                      <div className="pro-feature-text">
                        <strong>{t('proFeatureDaily')}</strong>
                        <span>New challenge every day</span>
                      </div>
                    </div>
                  </div>
                  <button className="pro-upsell-btn" onClick={() => {
                    const stripeLink = import.meta.env.VITE_STRIPE_PRO_LINK;
                    if (stripeLink) window.open(stripeLink, '_blank');
                    setShowProUpsell(false);
                  }}>
                    {t('upgradeTo')}
                  </button>
                  <button className="pro-upsell-close" onClick={() => setShowProUpsell(false)}>
                    {t('noThanks')}
                  </button>
                </div>
              </div>
            )}

            <button className="daily-btn" onClick={() => {
              const today = getTodayDate();
              const config = getDailyChallengeConfig(today);
              const rng = createDailyRng(today);
              setDailyMode({ date: today, config });
              trackDailyChallengeStarted({ playerCount: config.playerCount, difficulty: config.difficulty });
              initializeGame({
                difficulty: config.difficulty,
                playerCount: config.playerCount,
                cardsPerHand: config.cardsPerHand,
                rng,
                daily: true,
              });
            }}>
              {t('dailyChallenge')}
              <span className="daily-config">
                {(() => {
                  const config = getDailyChallengeConfig(getTodayDate());
                  return `${config.playerCount}p · ${config.cardsPerHand} cards · ${config.difficulty}`;
                })()}
              </span>
            </button>
          </div>
        </div>
      ) : (
        <div className="table">
          {dailyMode && <div className="daily-badge">DAILY</div>}

          <div className={`direction-pip ${gameState.gameDirection === -1 ? 'ccw' : ''}`}>
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path d="M12 4c4.41 0 8 3.59 8 8h-3l4 4 4-4h-3c0-5.52-4.48-10-10-10S2 6.48 2 12h2c0-4.41 3.59-8 8-8z" fill="currentColor"/>
            </svg>
          </div>

          {/* AI players arranged around the table */}
          {gameState.players.slice(1).map((player, i) => {
            const aiCount = gameState.players.length - 1;
            const seatClass = getAISeatClass(i, aiCount);
            return (
              <div key={player.id} className={`seat ${seatClass}`}>
                <PlayerSeat
                  player={player}
                  isActive={gameState.currentPlayerIndex === player.id}
                  isWinner={gameState.gameOver && gameState.winner?.id === player.id}
                />
              </div>
            );
          })}

          {/* Center area */}
          <div className="table-center">
            <div className="sum-display">
              <span className="sum-value" style={{ color: getSumColor() }}>
                {gameState.currentSum}
              </span>
              <span className="sum-limit">/ 99</span>
              <div className="sum-bar">
                <div
                  className="sum-fill"
                  style={{
                    width: `${(gameState.currentSum / 99) * 100}%`,
                    backgroundColor: getSumColor()
                  }}
                />
              </div>
            </div>

            <div className="center-decks">
              {/* Draw pile */}
              <div className="pile draw-pile">
                <div className="pile-cards">
                  {[...Array(Math.min(3, gameState.deck.length))].map((_, i) => (
                    <div
                      key={i}
                      className="pile-card card-back-small"
                      style={{ transform: `translateY(${-i * 2}px)`, zIndex: i }}
                    />
                  ))}
                </div>
                <span className="pile-label">{gameState.deck.length}</span>
              </div>

              {/* Discard pile */}
              <div className="pile discard-pile">
                <div className="pile-cards">
                  {playedCards.slice(-5).map((card, i) => {
                    let hash = 0;
                    const cardId = card.id || `card_${i}`;
                    for (let j = 0; j < cardId.length; j++) {
                      hash = ((hash << 5) - hash + cardId.charCodeAt(j)) & 0xffffffff;
                    }
                    const r1 = Math.abs(hash % 1000) / 1000;
                    const r2 = Math.abs((hash >> 8) % 1000) / 1000;
                    const r3 = Math.abs((hash >> 16) % 1000) / 1000;
                    const rx = (r1 * 12) - 6;
                    const ry = (r2 * 12) - 6;
                    const rr = (r3 * 40) - 20;

                    return (
                      <div
                        key={`${card.id}-${i}`}
                        className="pile-card discard-card"
                        style={{
                          transform: `translate(${rx}px, ${ry}px) rotate(${rr}deg)`,
                          zIndex: i + 1
                        }}
                      >
                        <span className="discard-value">{card.value}</span>
                      </div>
                    );
                  })}
                  {playedCards.length === 0 && (
                    <div className="pile-card empty-pile">
                      <span className="empty-text">-</span>
                    </div>
                  )}
                </div>
                <span className="pile-label">{playedCards.length}</span>
              </div>
            </div>

            {gameMessage && (
              <div className={`message ${gameMessage.key === 'yourTurn' ? 'your-turn' : ''}`}>
                {gameMessage.raw != null
                  ? gameMessage.raw
                  : [gameMessage.name, t(gameMessage.key), gameMessage.text].filter(Boolean).join(' ')
                }
              </div>
            )}

          </div>

          {/* Flying card (play) animation */}
          {flyingCard && (
            <div className={`flying-card fly-from-${flyingCard.direction}`}>
              <span className="flying-card-value">{flyingCard.card.value}</span>
            </div>
          )}

          {/* Drawing card animation */}
          {drawingCard && (
            <div className={`flying-card draw-to-${drawingCard.direction}`} />
          )}

          {/* Bottom player (You) */}
          <div className="seat seat-bottom">
            <div className="seat-info">
              <span className="seat-lives">
                {'♥'.repeat(gameState.players[0]?.score || 0)}
                {'♡'.repeat(3 - (gameState.players[0]?.score || 0))}
              </span>
            </div>
            {aceChoice ? (
              <div className="ace-prompt">
                <span className="ace-title">Ace</span>
                <div className="ace-buttons">
                  <button className="ace-btn" onClick={() => aceChoice.resolve(1)}>+1</button>
                  <button className="ace-btn" onClick={() => aceChoice.resolve(11)}>+11</button>
                </div>
              </div>
            ) : (
              <PlayerHand
                cards={playerHand}
                onCardSelect={setSelectedCard}
                onCardPlay={playCard}
                selectedCard={selectedCard}
                isPlayerTurn={gameState.currentPlayerIndex === 0 && !gameState.gameOver}
                currentSum={gameState.currentSum}
              />
            )}
          </div>

          {/* Game Over Overlay */}
          {gameState.gameOver && (
            <div className="game-over-overlay">
              <div className="game-over-box">
                <h2>{t('gameOver')}</h2>
                {gameState.winner && <p>{gameState.winner.name} {t('wins')}</p>}
                <button className="start-button" onClick={restartGame}>
                  {t('playAgain')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PlayerSeat({ player, isActive, isWinner }) {
  if (!player) return null;

  return (
    <div className={`player-seat ${isActive ? 'active' : ''} ${player.score <= 0 ? 'eliminated' : ''} ${isWinner ? 'winner' : ''}`}>
      <div className="seat-info">
        <span className={`seat-name ${isActive ? 'active' : ''}`}>{player.name}</span>
        <span className="seat-lives">
          {'♥'.repeat(player.score)}
          {'♡'.repeat(3 - player.score)}
        </span>
      </div>
      <div className="ai-cards">
        {player.hand?.map((_, i) => (
          <div
            key={i}
            className={`card-back-small ${isActive ? 'glow' : ''}`}
            style={{
              transform: `rotate(${(i - 1) * 10}deg) translateY(${Math.abs(i - 1) * 3}px)`,
              zIndex: i
            }}
          />
        ))}
      </div>
    </div>
  );
}

export default App;
